import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Calendar, MessageSquare, User, LogOut, Menu, X, Target, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';

export default function Layout({ children }: { children: React.ReactNode }) {
  const { profile, signOut } = useAuth();
  const { unreadMessages, pendingRequests } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Nav links — Messages carries a live badge count
  const NAV_LINKS = [
    { to: '/search', label: 'Find Partners', icon: Search, badge: 0 },
    { to: '/schedule', label: 'Schedule', icon: Calendar, badge: 0 },
    { to: '/chat', label: 'Messages', icon: MessageSquare, badge: unreadMessages },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* @section: navbar */}
      <header
        className="sticky top-0 z-50 border-b border-border bg-background/95"
        style={{ boxShadow: '0 2px 12px color-mix(in srgb, var(--primary) 8%, transparent)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <div
                className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center"
                style={{ boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 40%, transparent)' }}
              >
                <Target className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg text-foreground group-hover:text-primary transition-colors duration-200">
                Pivot-Mate
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map(({ to, label, icon: Icon, badge }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      active
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {/* Icon with notification dot */}
                    <span className="relative">
                      <Icon className="w-4 h-4" />
                      <AnimatePresence>
                        {badge > 0 && (
                          <motion.span
                            key="msg-dot"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                            className="absolute -top-1 -right-1 flex items-center justify-center"
                          >
                            {badge <= 9 ? (
                              <span
                                className="min-w-[16px] h-4 rounded-full bg-destructive text-[10px] font-bold text-white flex items-center justify-center px-1 leading-none"
                                style={{ boxShadow: '0 0 0 2px var(--background)' }}
                              >
                                {badge}
                              </span>
                            ) : (
                              <span
                                className="min-w-[16px] h-4 rounded-full bg-destructive text-[10px] font-bold text-white flex items-center justify-center px-1 leading-none"
                                style={{ boxShadow: '0 0 0 2px var(--background)' }}
                              >
                                9+
                              </span>
                            )}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                    {label}
                  </Link>
                );
              })}
            </nav>

            {/* User menu (desktop) */}
            <div className="hidden md:flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative flex items-center gap-2 p-1.5 rounded-xl hover:bg-muted transition-colors duration-200">
                    {/* Avatar with friend-request dot */}
                    <span className="relative">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={profile?.avatar_url ?? undefined} />
                        <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                          {profile?.full_name?.[0] ?? profile?.username?.[0] ?? '?'}
                        </AvatarFallback>
                      </Avatar>
                      <AnimatePresence>
                        {pendingRequests > 0 && (
                          <motion.span
                            key="req-dot"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                            className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-accent border-2 border-background"
                          />
                        )}
                      </AnimatePresence>
                    </span>
                    <span className="text-sm font-medium text-foreground max-w-[120px] truncate">
                      {profile?.full_name || profile?.username}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {pendingRequests > 0 && (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate('/search')}
                        className="cursor-pointer gap-2 text-accent focus:text-accent focus:bg-accent/10"
                      >
                        <UserPlus className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                        <span className="text-xs font-semibold">
                          {pendingRequests} partner request{pendingRequests > 1 ? 's' : ''} pending
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile hamburger — with combined dot if anything pending */}
            <button
              className="relative md:hidden p-2 rounded-lg hover:bg-muted"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              <AnimatePresence>
                {(unreadMessages > 0 || pendingRequests > 0) && !mobileOpen && (
                  <motion.span
                    key="mobile-dot"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                    className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-destructive border-2 border-background"
                  />
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden border-t border-border bg-background px-4 pb-4 pt-2"
          >
            {NAV_LINKS.map(({ to, label, icon: Icon, badge }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
              >
                <span className="relative">
                  <Icon className="w-4 h-4" />
                  {badge > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[15px] h-3.5 rounded-full bg-destructive text-[9px] font-bold text-white flex items-center justify-center px-0.5 leading-none"
                      style={{ boxShadow: '0 0 0 1.5px var(--background)' }}
                    >
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </span>
                {label}
                {badge > 0 && (
                  <span className="ml-auto text-xs font-semibold text-destructive">
                    {badge} new
                  </span>
                )}
              </Link>
            ))}

            {/* Mobile user row */}
            <div className="mt-2 pt-2 border-t border-border flex items-center gap-3 px-3 py-2">
              <button
                className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                onClick={() => { navigate('/profile'); setMobileOpen(false); }}
              >
                <span className="relative flex-shrink-0">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={profile?.avatar_url ?? undefined} />
                    <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                      {profile?.full_name?.[0] ?? profile?.username?.[0] ?? '?'}
                    </AvatarFallback>
                  </Avatar>
                  {pendingRequests > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-accent border-2 border-background" />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{profile?.full_name || profile?.username}</p>
                  <p className="text-xs text-muted-foreground">View profile</p>
                  {pendingRequests > 0 && (
                    <p className="text-xs text-accent font-medium">
                      {pendingRequests} partner request{pendingRequests > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="text-destructive hover:text-destructive flex-shrink-0"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
}
